import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { listPurposes } from './api';
import {
  emptyForm,
  isComplete,
  problemKey,
  resolvedType,
  toInput,
  type NewReportForm,
  type NewReportProblem,
} from './new-report-form';
import { ClientField, ProjectsField } from './NoteAllocationFields';
import { NoteFormFiles } from './NoteFormFiles';
import {
  NoteSaveProblem,
  NoteSaveMissed,
  parityProblem,
  type ParityProblem,
} from './NoteSaveNotices';
import { PurposeFields, TripFields } from './NewReportFields';
import { PHONE_FULL_PAGE } from './phone-sheet';
import { useNoteParityForm } from './use-note-parity-form';
import type { PurposeChoice, Report } from './types';

const EDIT_PROBLEMS = [
  'MONTH_CLOSED',
  'EXPENSE_OUT_OF_PERIOD',
  'CATEGORY_NOT_ALLOWED_FOR_NOTE',
  'REPORT_NOT_EDITABLE',
  'REPORT_TOO_LARGE',
  'TRIP_TOO_LONG',
  'ALLOWANCE_IN_CLOSED_MONTH',
] as const;
type EditProblem = (typeof EDIT_PROBLEMS)[number] | 'other';

type Load = 'loading' | 'failed' | 'ready';

/** The owner's usable purposes; Gestione filing for someone gets that person's list. */
function usePurposes(open: boolean, ownerUserId: string | undefined) {
  const [purposes, setPurposes] = useState<PurposeChoice[]>([]);
  const [load, setLoad] = useState<Load>('loading');
  useEffect(() => {
    if (!open) return;
    let live = true;
    setLoad('loading');
    void listPurposes(ownerUserId).then((outcome) => {
      if (!live) return;
      if (outcome.ok) setPurposes(outcome.value);
      setLoad(outcome.ok ? 'ready' : 'failed');
    });
    return () => {
      live = false;
    };
  }, [open, ownerUserId]);
  return { purposes, load } as const;
}

/**
 * «Nuova nota»: the reason, the purpose, the type when the purpose allows two, the month (the
 * current one or a later one) and, for a Trasferta, its dates and Italia/Estero (wave 2, R-a).
 * `period` is the first month offered («2026-10»); `ownerUserId` is the person Gestione files for.
 */
export function NewReportDialog({
  open,
  period,
  ownerUserId,
  onClose,
  onCreated,
  report,
  onStale,
  onReturnFocus,
}: {
  readonly onReturnFocus?: () => void;
  readonly report?: Report;
  readonly onStale?: () => void;
  readonly open: boolean;
  readonly period: string;
  readonly ownerUserId?: string | undefined;
  readonly onClose: () => void;
  readonly onCreated: (report: Report) => void;
}) {
  const { t } = useTranslation('expenses');
  const [form, setForm] = useState<NewReportForm>(() =>
    report
      ? {
          title: report.title,
          month: report.periodMonth,
          purposeId: report.purpose?.id ?? '',
          noteType: report.noteType ?? '',
          tripStart: report.tripStart ?? '',
          tripEnd: report.tripEnd ?? '',
          tripScope: report.tripScope ?? '',
        }
      : emptyForm(period),
  );
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<NewReportProblem | null>(null);
  const [editError, setEditError] = useState<EditProblem | null>(null);
  const [parityError, setParityError] = useState<ParityProblem | null>(null);
  // A new note a save created while a later step failed: the form now edits it (#915 W1).
  const [saved, setSaved] = useState<Report | undefined>(undefined);
  // After a partial save the saved note is newer than the one the page opened.
  const target = saved ?? report;
  const parity = useNoteParityForm(open, target);
  const { purposes, load } = usePurposes(open, ownerUserId);
  const purpose = purposes.find((p) => p.id === form.purposeId);
  const type = resolvedType(purpose, form.noteType);
  // A note sent back keeps its month (decision 196): the API takes only the same one.
  const sentBack = target?.status === 'more_info_requested';
  const editHelp = sentBack ? 'reports.edit.helpSentBack' : 'reports.edit.help';
  const help = target ? editHelp : 'reports.new.help';
  const complete = isComplete(form, purpose) && parity.savable;
  const close = (): void => (saved ? onCreated(saved) : onClose());

  useEffect(() => {
    setForm((current) => ({ ...current, month: period }));
  }, [period]);
  // Another owner, another list: a purpose the new list lacks is dropped.
  useEffect(() => {
    if (load === 'ready') {
      setForm((current) =>
        purposes.some((p) => p.id === current.purposeId)
          ? current
          : { ...current, purposeId: '', noteType: '' },
      );
    }
  }, [load, purposes]);

  const change = (patch: Partial<NewReportForm>): void => {
    setProblem(null);
    setEditError(null);
    setParityError(null);
    setForm((current) => ({ ...current, ...patch }));
  };

  const create = async (): Promise<void> => {
    if (type === null || busy || !complete) return;
    setBusy(true);
    setProblem(null);
    setEditError(null);
    setParityError(null);
    const input = toInput(form, type, target ? undefined : ownerUserId);
    const outcome = await parity.save(input);
    setBusy(false);
    if (!outcome.ok) {
      if (target && outcome.code === 'VERSION_CONFLICT') {
        if (report) onStale?.();
        else setEditError('other');
        return;
      }
      const known = problemKey(outcome.code);
      const allocationProblem = parityProblem(outcome.code);
      if (allocationProblem !== null) setParityError(allocationProblem);
      else if (target && known === 'other') {
        setEditError(EDIT_PROBLEMS.find((code) => code === outcome.code) ?? 'other');
      } else setProblem(known);
      return;
    }
    if (outcome.missed.length > 0) {
      setSaved(outcome.report);
      return;
    }
    setForm(emptyForm(period));
    onCreated(outcome.report);
  };

  return (
    <Sheet open={open} onOpenChange={(next) => (next ? undefined : close())}>
      <SheetContent
        side="bottom"
        className={`${PHONE_FULL_PAGE} flex flex-col gap-4 p-4`}
        onCloseAutoFocus={(event) => {
          if (onReturnFocus) {
            event.preventDefault();
            onReturnFocus();
          }
        }}
      >
        <SheetTitle>{t(target ? 'reports.edit.title' : 'reports.new.title')}</SheetTitle>
        <SheetDescription>{t(help)}</SheetDescription>
        <form
          className="flex flex-col gap-3 [&_input]:min-h-11 [&_button]:min-h-11 [&_[role=combobox]]:min-h-11"
          onSubmit={(event) => {
            event.preventDefault();
            void create();
          }}
        >
          <label className="flex flex-col gap-1 text-sm font-semibold">
            {t('reports.new.reason')}
            <Input
              value={form.title}
              maxLength={200}
              onChange={(event) => change({ title: event.target.value })}
              placeholder={t('reports.new.placeholder')}
            />
          </label>
          {load === 'failed' ? (
            <p role="alert" className="text-destructive text-sm">
              {t('reports.new.purposesFailed')}
            </p>
          ) : null}
          {load === 'ready' && purposes.length === 0 ? (
            <p role="status" className="text-muted-foreground text-sm">
              {t('reports.new.noPurposes')}
            </p>
          ) : null}
          <PurposeFields
            form={form}
            purposes={purposes}
            firstMonth={period}
            monthFixed={sentBack}
            onChange={change}
          />
          <TripFields form={form} purposes={purposes} onChange={change} />
          <ProjectsField
            allocation={parity.allocation}
            list={parity.projects}
            onChange={(projects) => {
              setParityError(null);
              parity.setAllocation((current) => ({ ...current, projects }));
            }}
          />
          <ClientField
            allocation={parity.allocation}
            list={parity.clients}
            onChange={(client) => {
              setParityError(null);
              parity.setAllocation((current) => ({ ...current, client }));
            }}
          />
          <NoteFormFiles
            saved={parity.savedFiles}
            removals={parity.removals}
            onToggleRemoval={parity.toggleRemoval}
            files={parity.files}
          />
          <NoteSaveMissed missed={parity.missed} />
          <NoteSaveProblem problem={parityError} />
          {problem ? (
            <p role="alert" className="text-destructive text-sm">
              {t(`reports.new.problems.${problem}`)}
            </p>
          ) : null}
          {editError ? (
            <p role="alert" className="text-destructive text-sm">
              {t(`reports.edit.problems.${editError}`)}
            </p>
          ) : null}
          <div className="flex gap-2">
            <Button type="submit" disabled={load !== 'ready' || !complete || busy}>
              {t(target ? 'reports.edit.save' : 'reports.new.create')}
            </Button>
            <Button type="button" variant="outline" disabled={busy} onClick={close}>
              {t('reports.new.cancel')}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
